/**
 * `/offers*` — the SUPPLIER side of "technical agreement on an offer"
 * (`docs/V2_OFFER_TECH_APPROVAL.md`, PR2). Every route requires a bearer token (`JwtAuthGuard` on
 * the server, `apps/api/src/offers/offers.controller.ts`), so every call in this module goes
 * through `authFetch` — none of it is exempt from `tokenless-calls.test.ts`'s scan.
 *
 * Mirrors `riders-api.ts`'s own shape (one function per endpoint, response types matching the
 * server's DTOs) rather than inventing a different convention for a sibling resource.
 */
import type { OfferAnswer, OfferDraft, OfferLineRef, OfferWithdrawReason, Rider, StagePatchConfig } from "@scb/schema";
import { authFetch, parseOrThrow } from "./http";
import { sharedGet } from "./request-share";
import type { PlacementBody } from "../offer/offer-placement";
import type { PublicationPower } from "./riders-api";

export interface OfferPartyResponse {
  displayName: string | null;
  company: string | null;
  email?: string;
}

export interface OfferApprovalResponse {
  state: string;
  by?: string;
  at?: string;
  comment?: string;
}

/** One append-only entry in `offer.history` (`apps/api/src/offers/offer.entity.ts`'s
 *  `OfferHistoryEntry` — mirrored here rather than imported, same reasoning as `OfferLineWire`
 *  below: the entity is API-internal, this is the wire shape). PR3's review page renders this as a
 *  compact list. */
export interface OfferHistoryEntryWire {
  at: string;
  actor: string;
  event: "submitted" | "changes_requested" | "approved" | "declined" | "withdrawn";
  comment?: string;
}

/** Wire shape of one answered line — `OfferLine` from `@scb/schema`, but dates/refs travel as
 *  plain JSON, so this is its own (structurally identical) type rather than importing the zod
 *  `OfferLine` inference, which is exactly what it infers to anyway. */
export interface OfferLineWire {
  ref: OfferLineRef;
  answer: OfferAnswer;
  model?: string;
  /** Catalogue link of a substitute (contract 2.14.0, SCRUM-610). */
  model_ids?: number[];
  comment?: string;
}

/** `GET/POST /offers*`'s full detail shape (`OffersController#toDetailResponse`) — the offer
 *  itself plus its PINNED rider snapshot, so a caller never needs a second request to render it. */
export interface OfferDetailResponse {
  id: string;
  /** The supplier's running count, «Offer 01» (owner 06.10.2026). Optional: an older API sends none. */
  number?: number;
  riderId: string;
  riderVersion: number;
  discipline: string;
  lines: OfferLineWire[];
  /** The lines the supplier left at «—» on purpose (SCRUM-839); absent from an older API, which reads as none. */
  left_blank?: OfferLineRef[];
  comment: string;
  status: string; // OfferStatus, see @scb/schema
  /** The rental's one-tap reason when it withdrew the offer and gave one (SCRUM-865); absent from an older API. */
  withdrawReason?: string | null;
  round: number;
  submittedAt: string | null;
  technical: OfferApprovalResponse;
  organiser: OfferApprovalResponse;
  history: OfferHistoryEntryWire[];
  createdAt: string;
  updatedAt: string;
  rider: { data: Rider; computed: { power: PublicationPower }; planView: string | null };
  supplier: OfferPartyResponse;
  author: OfferPartyResponse;
  /** Which party the CALLER is for THIS offer (PR3) — `pages/offer-overlay.ts` dispatches on this
   *  to decide between the supplier's answer form and the author's review. */
  role: "supplier" | "author";
  /** SCRUM-433: the supplier is the rider's owner (an admin's offer on their own rider). Optional
   *  on the wire type so a response from an older API reads as a normal offer. */
  selfOffer?: boolean;
  /** SCRUM-433: true on a SUBMITTED self-offer — `role` stays "supplier", but the caller may also
   *  act as the author, so the offer screen shows the "As supplier / As author" switch. */
  alsoAuthor?: boolean;
  /** SCRUM-936: the caller is the supplier and uploaded this rider's PDF — the original goes beside the offer. */
  fromOwnPdf?: boolean;
  /** SCRUM-587 S3: which show the offer joined and how, when it was sent with a date or venue. */
  attach?: { showId: string; how: string } | null;
  /** The date and venue the supplier sent; null when they sent none. */
  offered?: { when: unknown; venue: { id: string; name: string; city: string | null } | null } | null;
  /** The show's CURRENT date (SCRUM-587 decision 25) — follows the show; `offered.when` stays the supplier's own words. */
  showWhen?: unknown;
}

/** `GET /offers/mine` / `GET /offers/incoming`'s row shape — enough for a list, no snapshot.
 *  `supplierDisplayName`/`supplierCompany` (PR3) are populated on both endpoints — see the API's
 *  own doc comment on `OfferSummaryResponse` for why "mine" carries them too. */
export interface OfferSummaryResponse {
  id: string;
  riderId: string;
  riderTitle: string;
  riderVersion: number;
  status: string;
  round: number;
  updatedAt: string;
  supplierDisplayName: string | null;
  supplierCompany: string | null;
  /** PR3 fix: the fallback identity when `supplierDisplayName` is null. */
  supplierEmail: string | null;
  /** SCRUM-433: the supplier is the rider's owner. Optional so an older API row reads as false. */
  selfOffer?: boolean;
  /** SCRUM-936: the supplier answers its own PDF upload — no admin self-offer mark. */
  fromOwnPdf?: boolean;
  /** SCRUM-587 S4: the show the offer sits on, if any. Optional so an older API row reads as none. */
  showId?: string | null;
  /** The rider changed under this offer and the supplier has not re-confirmed yet. */
  riderChanged?: { answered: number; current: number } | null;
  /** The offer WAS approved when the rider changed under it: it waits for re-approval and still holds the warehouse (SCRUM-859). */
  riderChangeWasApproved?: boolean;
  /** The author's reason, when the offer was declined and one was given. */
  declineReason?: string | null;
  /** The rental's one-tap reason, when it withdrew the offer and gave one (SCRUM-865). */
  withdrawReason?: string | null;
  /** «A new offer for this show» (SCRUM-878): the withdrawn offer this DRAFT replaces; it is sent into that offer's show. Supplier only. */
  replacesOfferId?: string | null;
}

/** `POST /offers` — idempotent: one LIVE offer per `(rider, supplier, 'sound', show)`, an existing one is returned, never
 *  duplicated. With `showLink` (the `?show=` token) the answer is the offer for THAT show (decision 50: a decline
 *  closes the offer for one show only); without it, the unplaced draft or the newest offer. With `replaces` (the id of the
 *  caller's own WITHDRAWN offer, SCRUM-878) the answer is the live offer in that offer's show, else a draft that will be sent there.
 *  400s if the caller owns the rider; 404/410 if the version isn't a live publication. */
export async function createOrGetOffer(riderId: string, riderVersion: number, showLink?: string | null, replaces?: string | null): Promise<OfferDetailResponse> {
  const res = await authFetch(`/offers`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ riderId, riderVersion, ...(showLink ? { showLink } : {}), ...(replaces ? { replaces } : {}) }),
  });
  return parseOrThrow<OfferDetailResponse>(res);
}

/** `GET /offers/:id` — the supplier always; the rider author once it is submitted; nobody else. */
export async function getOffer(id: string): Promise<OfferDetailResponse> {
  const res = await authFetch(`/offers/${encodeURIComponent(id)}`);
  return parseOrThrow<OfferDetailResponse>(res);
}

/** `GET/PUT /offers/:id/patch` — the stage patch kept on the offer (owner, 27.10.2026): the authored part only
 *  (`StagePatchConfig`); the routes are derived on screen. `canEdit`: the supplier, while the offer is not declined. */
export interface OfferPatchResponse {
  patch: StagePatchConfig | null;
  updatedAt: string | null;
  canEdit: boolean;
}

export async function getOfferPatch(id: string): Promise<OfferPatchResponse> {
  const res = await authFetch(`/offers/${encodeURIComponent(id)}/patch`);
  return parseOrThrow<OfferPatchResponse>(res);
}

/** Supplier only; 400 if the body is not a `StagePatchConfig`, 404 for anyone else, 409 once the offer is declined. */
export async function putOfferPatch(id: string, config: StagePatchConfig): Promise<OfferPatchResponse> {
  const res = await authFetch(`/offers/${encodeURIComponent(id)}/patch`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(config),
  });
  return parseOrThrow<OfferPatchResponse>(res);
}

/** `PUT /offers/:id` — replaces the draft. Supplier-only, only while `draft`/`changes_requested`
 *  (409 otherwise). Callers should run `validateOfferDraft()` (`@scb/schema`) first — the server
 *  re-validates independently, but a client-side check gives the supplier a same-screen message
 *  instead of a round trip for a mistake the schema already knows about. */
export async function putOfferDraft(id: string, draft: OfferDraft): Promise<OfferDetailResponse> {
  const res = await authFetch(`/offers/${encodeURIComponent(id)}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(draft),
  });
  return parseOrThrow<OfferDetailResponse>(res);
}

/** `POST /offers/:id/submit`. With `placement` (SCRUM-587 S3: the «When and where» block) the server
 *  finds the band's show or makes one and answers `attach`; without it NO body is sent, exactly as
 *  before. */
export async function submitOffer(id: string, placement?: PlacementBody): Promise<OfferDetailResponse> {
  const res = await authFetch(
    `/offers/${encodeURIComponent(id)}/submit`,
    placement
      ? { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(placement) }
      : { method: "POST" }
  );
  return parseOrThrow<OfferDetailResponse>(res);
}

/** `GET /offers/mine` — the caller's own offers, as a supplier. */
export async function listMyOffers(): Promise<OfferSummaryResponse[]> {
  return sharedGet("GET /offers/mine", async () => parseOrThrow<OfferSummaryResponse[]>(await authFetch(`/offers/mine`)));
}

/** `GET /offers/incoming` — offers on riders the caller AUTHORS, drafts excluded (PR3). */
export async function listIncomingOffers(): Promise<OfferSummaryResponse[]> {
  return sharedGet("GET /offers/incoming", async () => parseOrThrow<OfferSummaryResponse[]>(await authFetch(`/offers/incoming`)));
}

/** `POST /offers/:id/approve` — rider-author only, only while `submitted` (409 otherwise). Final
 *  in v1: nothing moves an approved offer again (PR3, `docs/V2_OFFER_TECH_APPROVAL.md`). */
export async function approveOffer(id: string, round?: number): Promise<OfferDetailResponse> {
  // `round` = the round the author is LOOKING AT (SCRUM-431): the server 409s if the supplier has
  // resubmitted since, instead of approving answers the author never saw. Optional on the wire.
  const res = await authFetch(`/offers/${encodeURIComponent(id)}/approve`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(round === undefined ? {} : { round }),
  });
  return parseOrThrow<OfferDetailResponse>(res);
}

/** `POST /offers/:id/request-changes` — rider-author only, only while `submitted`. `comment` is
 *  REQUIRED (400 if missing/blank) — a rejection with no reason is not a usable one. */
export async function requestChangesOffer(id: string, comment: string, round?: number): Promise<OfferDetailResponse> {
  const res = await authFetch(`/offers/${encodeURIComponent(id)}/request-changes`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(round === undefined ? { comment } : { comment, round }),
  });
  return parseOrThrow<OfferDetailResponse>(res);
}

/** `POST /offers/:id/decline` — rider-author only; the author's FINAL no (SCRUM-587). `reason` is optional.
 *  Allowed from submitted / changes_requested / awaiting_organiser; the supplier cannot edit or resubmit after. */
export async function declineOffer(id: string, reason?: string, round?: number): Promise<OfferDetailResponse> {
  const body: { reason?: string; round?: number } = {};
  if (reason?.trim()) body.reason = reason.trim();
  if (round !== undefined) body.round = round;
  const res = await authFetch(`/offers/${encodeURIComponent(id)}/decline`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  return parseOrThrow<OfferDetailResponse>(res);
}

/** `POST /offers/:id/withdraw` — the SUPPLIER takes its own offer back (SCRUM-865), from any sent status, an approved one
 *  included. `reason` is the optional one-tap word (price / dates / other); none is fine. Final: a new offer is the only way on. */
export async function withdrawOffer(id: string, reason?: OfferWithdrawReason): Promise<OfferDetailResponse & { told?: string[] }> {
  const res = await authFetch(`/offers/${encodeURIComponent(id)}/withdraw`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(reason ? { reason } : {}),
  });
  return parseOrThrow<OfferDetailResponse & { told?: string[] }>(res);
}

/** `POST /offers/:id/organiser-approve` — the organiser's second approval (SCRUM-587 §4c). Only the show's organiser
 *  party who is not the rider's author may call it (404 for anyone else); the answer is narrow. */
export async function organiserApproveOffer(id: string): Promise<{ id: string; status: string }> {
  const res = await authFetch(`/offers/${encodeURIComponent(id)}/organiser-approve`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({}),
  });
  return parseOrThrow<{ id: string; status: string }>(res);
}

/** `POST /offers/:id/organiser-request-changes` — `comment` is required (400 otherwise). */
export async function organiserRequestChangesOffer(id: string, comment: string): Promise<{ id: string; status: string }> {
  const res = await authFetch(`/offers/${encodeURIComponent(id)}/organiser-request-changes`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ comment }),
  });
  return parseOrThrow<{ id: string; status: string }>(res);
}
